import type { PageBlock } from "@/utils/customPageStore";
import AdminEditField from "@/components/feature/AdminEditField";
import AdminImageUpload from "@/components/feature/AdminImageUpload";
import {
  SectionAdminBar,
  useBlockConfig,
  ArrayDeleteButton,
  AddItemButton,
  AdminCTAButton,
} from "./shared";

interface GradientQuoteBlockProps {
  block: PageBlock;
  isAdmin: boolean;
  isDark: boolean;
  headingFont: string;
  headingStyle: React.CSSProperties;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
  onUpdate?: (b: PageBlock) => void;
}

export default function GradientQuoteBlock({
  block,
  isAdmin,
  isDark,
  headingFont,
  headingStyle,
  onDelete,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  onUpdate,
}: GradientQuoteBlockProps) {
  const { get, setConfig } = useBlockConfig(block, onUpdate);
  const headingColor = isDark ? "text-white" : "text-gray-900";
  const subColor = isDark ? "text-gray-400" : "text-gray-600";

  const heading = get("heading", "");
  const subheading = get("subheading", "");
  const body = get("body", "");
  const quote = get("quote", "");
  const quoteAuthor = get("quoteAuthor", "");
  const infoCards = get("infoCards", []) as { icon: string; color: string; title: string; desc: string }[];
  const photos = get("photos", []) as { src: string; alt: string }[];
  const appButtonLabel = get("appButtonLabel", "Start Your Application");
  const appButtonUrl = get("appButtonUrl", "/#application");

  const setInfoCard = (idx: number, key: "icon" | "color" | "title" | "desc", val: string) => {
    const newCards = infoCards.map((card, i) => (i === idx ? { ...card, [key]: val } : card));
    setConfig("infoCards", newCards);
  };

  const deleteInfoCard = (idx: number) => {
    setConfig("infoCards", infoCards.filter((_, i) => i !== idx));
  };

  const addInfoCard = () => {
    setConfig("infoCards", [...infoCards, { icon: "ri-star-line", color: "text-pink-500", title: "New Card", desc: "Description here..." }]);
  };

  const setPhoto = (idx: number, key: "src" | "alt", val: string) => {
    const newPhotos = photos.map((p, i) => (i === idx ? { ...p, [key]: val } : p));
    setConfig("photos", newPhotos);
  };

  const addPhoto = () => {
    setConfig("photos", [...photos, { src: "", alt: "" }]);
  };

  const deletePhoto = (idx: number) => {
    setConfig("photos", photos.filter((_, i) => i !== idx));
  };

  return (
    <div className={`mb-8 ${isAdmin ? "rounded-xl overflow-hidden border border-green-200" : ""}`}>
      {isAdmin && (
        <SectionAdminBar
          layout="gradient-quote"
          isDark={isDark}
          onDelete={onDelete}
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
          isFirst={isFirst}
          isLast={isLast}
        />
      )}
      <div className="py-12 px-6 rounded-2xl" style={{ background: isDark ? "#1f2937" : "#fff" }}>
        <div className="flex flex-col lg:flex-row gap-10">
          {/* Left panel */}
          <div className="w-full lg:w-2/5 flex-shrink-0">
            <div className="rounded-3xl overflow-hidden" style={{ background: "linear-gradient(135deg, #f9a8d4 0%, #86efac 100%)" }}>
              <div className="p-7">
                {photos.length > 0 && (
                  <div className="grid grid-cols-2 gap-3 mb-5">
                    {photos.map((photo, i) => (
                      <div key={i} className="w-full h-24 rounded-xl overflow-hidden relative group/photo">
                        {isAdmin && onUpdate && (
                          <button
                            onClick={(e) => { e.stopPropagation(); deletePhoto(i); }}
                            className="absolute top-1 right-1 z-10 w-5 h-5 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded-full cursor-pointer shadow-sm transition-colors opacity-0 group-hover/photo:opacity-100"
                          >
                            <i className="ri-close-line" style={{ fontSize: "8px" }}></i>
                          </button>
                        )}
                        {isAdmin && onUpdate ? (
                          <AdminImageUpload
                            src={photo.src || ""}
                            alt={photo.alt}
                            onReplace={(url) => setPhoto(i, "src", url)}
                            className="w-full h-full"
                            imgClassName="w-full h-full object-cover"
                            isDark={isDark}
                          />
                        ) : (
                          <img src={photo.src} alt={photo.alt} className="w-full h-full object-cover" />
                        )}
                      </div>
                    ))}
                    {isAdmin && onUpdate && (
                      <button
                        onClick={(e) => { e.stopPropagation(); addPhoto(); }}
                        className="w-full h-24 rounded-xl border-2 border-dashed border-white/50 flex items-center justify-center text-white/70 hover:text-white cursor-pointer transition-colors"
                      >
                        <i className="ri-add-line text-lg"></i>
                      </button>
                    )}
                  </div>
                )}
                {quote && (
                  <AdminEditField value={quote} onSave={(v) => setConfig("quote", v)} multiline inputClassName="text-sm italic text-white">
                    <blockquote className="text-white text-sm italic leading-relaxed mb-2">&ldquo;{quote}&rdquo;</blockquote>
                  </AdminEditField>
                )}
                {quoteAuthor && (
                  <AdminEditField value={quoteAuthor} onSave={(v) => setConfig("quoteAuthor", v)} inputClassName="text-xs text-white/80 font-bold">
                    <p className="text-white/80 text-xs font-bold">{quoteAuthor}</p>
                  </AdminEditField>
                )}
              </div>
            </div>

            {infoCards.length > 0 && (
              <div className="mt-5 space-y-3">
                {infoCards.map((card, i) => (
                  <div
                    key={i}
                    className={`flex items-center gap-4 rounded-xl p-4 relative group/appcard ${
                      isDark ? "bg-gray-700" : "bg-pink-50"
                    }`}
                  >
                    {isAdmin && onUpdate && <ArrayDeleteButton onDelete={() => deleteInfoCard(i)} />}
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white flex-shrink-0">
                      {isAdmin && onUpdate ? (
                        <div className="flex flex-col items-center">
                          <i className={`${card.icon} ${card.color} text-xl`}></i>
                          <AdminEditField
                            value={card.icon}
                            onSave={(v) => setInfoCard(i, "icon", v)}
                            inputClassName="text-[8px] w-full text-center"
                          >
                            <span className="text-[8px] text-gray-400">{card.icon}</span>
                          </AdminEditField>
                        </div>
                      ) : (
                        <i className={`${card.icon} ${card.color} text-xl`}></i>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <AdminEditField
                        value={card.title}
                        onSave={(v) => setInfoCard(i, "title", v)}
                        inputClassName="font-bold text-sm"
                      >
                        <div className={`font-bold text-sm ${headingColor}`}>{card.title}</div>
                      </AdminEditField>
                      <AdminEditField
                        value={card.desc}
                        onSave={(v) => setInfoCard(i, "desc", v)}
                        inputClassName="text-xs"
                      >
                        <div className={`text-xs ${subColor}`}>{card.desc}</div>
                      </AdminEditField>
                    </div>
                  </div>
                ))}
                {isAdmin && onUpdate && <AddItemButton onClick={addInfoCard} label="Add Card" isDark={isDark} className="py-3" />}
              </div>
            )}
          </div>

          {/* Right panel */}
          <div className="w-full lg:w-3/5">
            {subheading && (
              <AdminEditField value={subheading} onSave={(v) => setConfig("subheading", v)} inputClassName="text-sm text-green-500 font-bold uppercase tracking-widest">
                <span className="text-green-500 text-sm font-bold uppercase tracking-widest">{subheading}</span>
              </AdminEditField>
            )}
            {heading && (
              <AdminEditField value={heading} onSave={(v) => setConfig("heading", v)} inputClassName="text-3xl font-extrabold">
                <h2 className={`text-3xl font-extrabold mt-2 mb-2 ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>
                  {heading}
                </h2>
              </AdminEditField>
            )}
            {body && (
              <AdminEditField value={body} onSave={(v) => setConfig("body", v)} multiline inputClassName="text-base">
                <p className={`mb-6 ${subColor}`}>{body}</p>
              </AdminEditField>
            )}
            <div className={`rounded-2xl p-8 text-center ${isDark ? "bg-gray-800" : "bg-pink-50"}`}>
              <p className={`text-sm ${subColor} mb-4`}>Ready to join our community of makers?</p>
              <div className="flex flex-col items-center gap-1.5">
                {isAdmin && onUpdate ? (
                  <>
                    <AdminEditField
                      value={appButtonLabel}
                      onSave={(v) => setConfig("appButtonLabel", v)}
                      inputClassName="text-center font-bold"
                    >
                      <AdminCTAButton variant="primary">{appButtonLabel}</AdminCTAButton>
                    </AdminEditField>
                    <AdminEditField
                      value={appButtonUrl}
                      onSave={(v) => setConfig("appButtonUrl", v)}
                      inputClassName="text-xs text-center text-gray-400"
                    >
                      <span className="text-xs text-gray-400">URL: {appButtonUrl}</span>
                    </AdminEditField>
                  </>
                ) : (
                  <button
                    onClick={() => { const url = appButtonUrl || "/#application"; if (url.startsWith("http")) { window.open(url, "_blank", "noopener noreferrer"); } else if (url.startsWith("#")) { const el = document.querySelector(url); if (el) { el.scrollIntoView({ behavior: "smooth" }); } else { window.location.href = `/${url}`; } } else { window.location.href = url; } }}
                    className="bg-green-500 hover:bg-green-600 text-white font-bold px-8 py-3 rounded-full cursor-pointer whitespace-nowrap transition-colors"
                  >
                    {appButtonLabel}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}